<aside
  *ngIf="canRender"
  class="billy-bar fixed inset-x-0 bottom-0 z-50 flex flex-col border-t backdrop-blur-xl"
  [ngClass]="[
    collapsed ? '' : 'billy-bar-expanded',
    isDarkMode
      ? 'border-gray-700/80 bg-gray-900/95 text-gray-100'
      : 'border-gray-200/80 bg-white/95 text-gray-900'
  ]"
  aria-label="Billy consumption bar">

  <div
    class="billy-bar-body order-last mx-auto w-full max-w-6xl overscroll-contain px-4 sm:px-6"
    [ngClass]="collapsed ? 'billy-bar-body-collapsed' : 'billy-bar-body-expanded'"
    [class.billy-bar-body--dropdown-open]="periodDropdownOpen">
    <div
      *ngIf="error"
      class="rounded-xl border px-3 py-2 text-sm"
      [ngClass]="isDarkMode ? 'border-amber-500/30 bg-amber-500/10 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'">
      Could not load consumption.
    </div>

    <div
      *ngIf="loading"
      class="rounded-xl border px-3 py-2 text-sm"
      [ngClass]="isDarkMode ? 'border-gray-800 bg-gray-800/60 text-gray-300' : 'border-gray-100 bg-gray-50/80 text-gray-600'">
      Recalculating consumption...
    </div>

    <ng-container *ngIf="!error && consumption">
      <!-- Minutes package progress bar (ticket #126): only shown for client views
           whose Billy profile has a contracted monthly minutes allowance configured -->
      <div
        *ngIf="consumption.minutes_package as minutesPackage"
        class="mb-3 rounded-xl border px-3 py-3"
        [ngClass]="isDarkMode ? 'border-gray-800 bg-gray-900/70' : 'border-gray-100 bg-white/80'">
        <div class="flex items-center justify-between text-xs" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
          <span class="font-medium uppercase tracking-[0.12em] text-[11px]">Minutes package</span>
          <span class="tabular-nums">{{ formatNumber(minutesPackage.consumed) }} / {{ formatNumber(minutesPackage.contracted) }} min</span>
        </div>
        <div class="mt-1.5 h-2 w-full overflow-hidden rounded-full" [ngClass]="isDarkMode ? 'bg-gray-800' : 'bg-emerald-100'">
          <div
            class="h-2 rounded-full transition-all duration-500 ease-in-out"
            [ngClass]="minutesPackage.over_allowance ? 'bg-red-500' : 'bg-emerald-500'"
            [style.width.%]="minutesPackage.usage_percent">
          </div>
        </div>
        <p class="mt-1.5 text-[11px]" [ngClass]="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
          <ng-container *ngIf="!minutesPackage.over_allowance">{{ formatNumber(minutesPackage.remaining) }} min remaining this period</ng-container>
          <ng-container *ngIf="minutesPackage.over_allowance">Package exceeded by {{ formatNumber(minutesPackage.consumed - minutesPackage.contracted) }} min</ng-container>
        </p>
      </div>

<!-- Rules-v2 balance: committed tier progress or prepaid bag. Overage is a
           billable amount (charged next period at the tier price), never an alert -->
      <div
        *ngIf="consumption.balance as balance"
        class="mb-3 rounded-xl border px-3 py-3"
        [ngClass]="isDarkMode ? 'border-gray-800 bg-gray-900/70' : 'border-gray-100 bg-white/80'">
        <ng-container *ngIf="balance.kind === 'committed_tier'">
          <div class="flex items-center justify-between text-xs" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
            <span class="font-medium uppercase tracking-[0.12em] text-[11px]">
              Contracted volume{{ balance.tier_name ? ' · ' + balance.tier_name : '' }}
            </span>
            <span class="tabular-nums">
              {{ formatBalanceUnits(balance.consumed_units, balance.unit) }} / {{ formatBalanceUnits(balance.contracted_units, balance.unit) }}
            </span>
          </div>
          <div class="mt-1.5 h-2 w-full overflow-hidden rounded-full" [ngClass]="isDarkMode ? 'bg-gray-800' : 'bg-emerald-100'">
            <div
              class="h-2 rounded-full bg-emerald-500 transition-all duration-500 ease-in-out"
              [style.width.%]="balance.usage_percent">
            </div>
          </div>
          <p class="mt-1.5 text-[11px]" [ngClass]="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
            <ng-container *ngIf="balance.overage_units === 0">
              {{ formatBalanceUnits(balance.remaining_units, balance.unit) }} remaining of the monthly commitment
            </ng-container>
            <ng-container *ngIf="balance.overage_units > 0">
              Overage: {{ formatBalanceUnits(balance.overage_units, balance.unit) }} at the same tier price
              ({{ formatCurrency(balance.overage_amount, consumption.currency) }}, billed next period)
            </ng-container>
          </p>
        </ng-container>
        <!-- Bolsa prepago con barra: lo que queda se ve vaciarse -->
        <ng-container *ngIf="balance.kind === 'prepaid_bag' && prepaidGauge as gauge">
          <div class="flex items-center justify-between gap-3 text-xs" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
            <span class="font-medium uppercase tracking-[0.12em] text-[11px]">Prepaid balance</span>
            <span class="tabular-nums">
              <span class="font-semibold" [ngClass]="isDarkMode ? 'text-gray-100' : 'text-gray-900'">{{ formatCurrency(gauge.availableEur, consumption.currency) }}</span>
              of {{ formatCurrency(gauge.sizeEur, consumption.currency) }}
            </span>
          </div>
          <div
            class="billy-prepaid-bar mt-1.5 h-2.5 w-full overflow-hidden rounded-full"
            [ngClass]="isDarkMode ? 'bg-gray-800' : 'bg-gray-100'"
            role="progressbar"
            aria-label="Prepaid balance left"
            aria-valuemin="0"
            aria-valuemax="100"
            [attr.aria-valuenow]="gauge.percent">
            <div
              class="h-full rounded-full transition-all duration-500 ease-in-out"
              [ngClass]="prepaidToneClass(gauge.tone)"
              [style.width.%]="gauge.percent">
            </div>
          </div>
          <p class="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] tabular-nums" [ngClass]="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
            <span>{{ gauge.percent }} % left<ng-container *ngIf="balance.available_units != null"> · ≈ {{ formatPrepaidUnits(balance.available_units, balance.unit) }}</ng-container></span>
            <span *ngIf="balance.consumed_period_eur != null">{{ formatCurrency(balance.consumed_period_eur, consumption.currency) }} consumed this period</span>
            <span *ngIf="gauge.validUntil">Valid until {{ gauge.validUntil }}</span>
          </p>
        </ng-container>
        <ng-container *ngIf="balance.kind === 'prepaid_bag' && !prepaidGauge">
          <div class="flex items-center justify-between text-xs" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
            <span class="font-medium uppercase tracking-[0.12em] text-[11px]">Prepaid balance</span>
            <span class="tabular-nums">
              <ng-container *ngIf="balance.available_eur != null; else prepaidUnitsOnly">
                <span class="font-semibold" [ngClass]="isDarkMode ? 'text-gray-100' : 'text-gray-900'">{{ formatCurrency(balance.available_eur, consumption.currency) }}</span> available
                <span *ngIf="balance.available_units != null"> · ≈ {{ formatPrepaidUnits(balance.available_units, balance.unit) }}</span>
              </ng-container>
              <ng-template #prepaidUnitsOnly>{{ formatBalanceUnits(balance.available_units, balance.unit) }} available</ng-template>
            </span>
          </div>
          <p class="mt-1.5 text-[11px] tabular-nums" [ngClass]="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
            <ng-container *ngIf="balance.consumed_period_eur != null; else prepaidConsumedUnitsOnly">
              {{ formatCurrency(balance.consumed_period_eur, consumption.currency) }} consumed this period<span *ngIf="balance.consumed_period_units != null"> · ≈ {{ formatPrepaidUnits(balance.consumed_period_units, balance.unit) }}</span>
            </ng-container>
            <ng-template #prepaidConsumedUnitsOnly>{{ formatBalanceUnits(balance.consumed_period_units, balance.unit) }} consumed this period</ng-template>
          </p>
        </ng-container>
      </div>

      <div class="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] lg:items-start lg:gap-4">
        <!-- Left column: filters + historical chart -->
        <div class="space-y-3">
          <div
            class="rounded-xl border"
            [ngClass]="isDarkMode ? 'border-gray-800 bg-gray-900/70' : 'border-gray-100 bg-white/80'">
            <button
              type="button"
              class="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              [ngClass]="isDarkMode ? 'hover:bg-gray-800/70' : 'hover:bg-gray-50'"
              [attr.aria-expanded]="!filtersCollapsed"
              aria-controls="billy-consumption-filters"
              (click)="toggleFiltersCollapsed()">
              <span
                class="text-[11px] font-semibold uppercase tracking-[0.16em]"
                [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
                Filter
              </span>
              <svg
                class="h-4 w-4 text-gray-400 transition-transform"
                [class.rotate-180]="!filtersCollapsed"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </button>

            <div
              *ngIf="!filtersCollapsed"
              id="billy-consumption-filters"
              class="grid gap-2 px-3 pb-3">
              <div class="text-xs" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
                <span class="block">Period</span>
                <div class="relative mt-1" data-billy-period-dropdown>
                  <button
                    type="button"
                    class="flex w-full items-center justify-between gap-2 rounded-lg border px-2 py-1.5 text-left text-sm outline-none transition focus:ring-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                    [ngClass]="isDarkMode ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-200 bg-white text-gray-900'"
                    [disabled]="loading"
                    [attr.aria-expanded]="periodDropdownOpen"
                    aria-haspopup="listbox"
                    (click)="togglePeriodDropdown()">
                    <span class="truncate">{{ selectedPeriodLabel }}</span>
                    <svg
                      class="h-4 w-4 shrink-0 text-gray-400 transition-transform"
                      [class.rotate-180]="periodDropdownOpen"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>

                  <ul
                    *ngIf="periodDropdownOpen"
                    role="listbox"
                    class="absolute bottom-full left-0 right-0 z-50 mb-1 max-h-56 overflow-y-auto rounded-lg border py-1 shadow-xl"
                    [ngClass]="isDarkMode ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-200 bg-white text-gray-900'">
                    <li *ngFor="let period of periodOptions; trackBy: trackPeriodByKey">
                      <button
                        type="button"
                        role="option"
                        [attr.aria-selected]="period.key === selectedPeriodKey"
                        class="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm transition"
                        [ngClass]="period.key === selectedPeriodKey
                          ? (isDarkMode ? 'bg-emerald-500/15 text-emerald-200' : 'bg-emerald-50 text-emerald-700')
                          : (isDarkMode ? 'hover:bg-gray-700/70' : 'hover:bg-gray-50')"
                        (click)="selectPeriod(period.key)">
                        <svg
                          class="h-3.5 w-3.5 shrink-0 text-emerald-500"
                          [class.invisible]="period.key !== selectedPeriodKey"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                        <span class="truncate">{{ period.label }}</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="grid grid-cols-1 items-end gap-2">
                <label class="min-w-0 text-xs" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
                  From
                  <input
                    type="date"
                    class="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm outline-none transition focus:ring-2 focus:ring-emerald-500"
                    [ngClass]="isDarkMode ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-200 bg-white text-gray-900'"
                    [(ngModel)]="customStart"
                    [disabled]="loading">
                </label>
                <label class="min-w-0 text-xs" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
                  To
                  <input
                    type="date"
                    class="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm outline-none transition focus:ring-2 focus:ring-emerald-500"
                    [ngClass]="isDarkMode ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-200 bg-white text-gray-900'"
                    [(ngModel)]="customEnd"
                    [disabled]="loading">
                </label>
                <button
                  type="button"
                  class="w-full rounded-lg px-3 py-1.5 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                  [ngClass]="isDarkMode ? 'bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30' : 'bg-emerald-600 text-white hover:bg-emerald-700'"
                  [disabled]="loading"
                  (click)="applyCustomRange()">
                  Apply
                </button>
              </div>

              <p
                *ngIf="filterError"
                class="text-xs"
                [ngClass]="isDarkMode ? 'text-amber-300' : 'text-amber-700'">
                {{ filterError }}
              </p>
            </div>
          </div>

          <div
            *ngIf="isAdminView"
            class="rounded-xl border"
            [ngClass]="isDarkMode ? 'border-gray-800 bg-gray-900/70' : 'border-gray-100 bg-white/80'">
            <button
              type="button"
              class="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              [ngClass]="isDarkMode ? 'hover:bg-gray-800/70' : 'hover:bg-gray-50'"
              [attr.aria-expanded]="showHistoryChart"
              aria-controls="billy-billing-history"
              [disabled]="historyLoading"
              (click)="toggleHistoryChart()">
              <span
                class="text-[11px] font-semibold uppercase tracking-[0.16em]"
                [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
                {{ showHistoryChart ? 'Hide historical chart' : 'View historical chart' }}
              </span>
              <svg
                class="h-4 w-4 text-gray-400 transition-transform"
                [class.rotate-180]="showHistoryChart"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </button>

            <div
              *ngIf="showHistoryChart"
              id="billy-billing-history"
              class="px-3 pb-3">
              <div
                *ngIf="historyLoading"
                class="rounded-lg px-3 py-2 text-sm"
                [ngClass]="isDarkMode ? 'bg-gray-800/70 text-gray-300' : 'bg-gray-50 text-gray-600'">
                Loading billing history...
              </div>
              <div
                *ngIf="historyError"
                class="rounded-lg px-3 py-2 text-sm"
                [ngClass]="isDarkMode ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-800'">
                Could not load billing history.
              </div>
              <div
                *ngIf="!historyLoading && !historyError"
                class="h-48 rounded-lg p-2"
                [ngClass]="isDarkMode ? 'bg-gray-950/40' : 'bg-gray-50/80'">
                <canvas id="billy-billing-history-chart"></canvas>
              </div>
            </div>
          </div>
        </div>

        <!-- Right column: agent breakdown + totals -->
        <div class="min-w-0">
          <div class="space-y-1.5">
            <div *ngFor="let project of breakdownGroups; trackBy: trackGroupByKey">
              <!-- Un proyecto con un solo agente no pinta nivel de más: la fila
                   del agente sube tal cual, igual que en las cuentas que no
                   trabajan por proyecto. -->
              <ng-container *ngIf="!project.hasAgentLevel"
                [ngTemplateOutlet]="agentCard"
                [ngTemplateOutletContext]="{ $implicit: project.agents[0], nested: false }">
              </ng-container>

              <!-- Project header row: subtotal siempre visible, agentes dentro -->
              <div *ngIf="project.hasAgentLevel"
                class="rounded-xl border px-3 py-2"
                [ngClass]="isDarkMode ? 'border-gray-800 bg-gray-800/60' : 'border-gray-100 bg-gray-50/80'">
                <div class="grid grid-cols-[minmax(0,1fr)_auto_8.5rem] items-center gap-3">
                  <div class="min-w-0 flex items-center gap-1.5">
                    <button type="button"
                      (click)="toggleProjectExpansion(project.key)"
                      class="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded transition-colors"
                      [ngClass]="isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-700'"
                      [title]="expandedProjects.has(project.key) ? 'Colapsar agentes' : 'Ver agentes'">
                      <span class="text-[10px] leading-none">{{ expandedProjects.has(project.key) ? '▾' : '▸' }}</span>
                    </button>
                    <p class="truncate text-sm font-semibold">{{ project.name }}</p>
                    <span class="flex-shrink-0 text-[10px] leading-none rounded px-1.5 py-0.5"
                      [ngClass]="isDarkMode ? 'bg-gray-700/70 text-gray-300' : 'bg-gray-200/80 text-gray-600'">
                      {{ project.agents.length }} agentes
                    </span>
                  </div>
                  <p class="text-right text-xs tabular-nums whitespace-nowrap" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
                    {{ project.unitsText }}
                  </p>
                  <div class="text-right">
                    <p class="text-sm font-semibold tabular-nums">
                      {{ formatCurrency(project.subtotal, currency) }}
                    </p>
                  </div>
                </div>

                <div *ngIf="expandedProjects.has(project.key)" class="mt-2 space-y-1 border-t pt-2"
                  [ngClass]="isDarkMode ? 'border-gray-700' : 'border-gray-200'">
                  <ng-container *ngFor="let agent of project.agents; trackBy: trackGroupByKey"
                    [ngTemplateOutlet]="agentCard"
                    [ngTemplateOutletContext]="{ $implicit: agent, nested: true }">
                  </ng-container>
                </div>
              </div>
            </div>

            <!-- Agent row: cabecera de agente + sus conceptos facturables.
                 `nested` sólo cambia el peso visual (dentro de un proyecto la
                 fila es secundaria); el comportamiento es el mismo. -->
            <ng-template #agentCard let-agent let-nested="nested">
              <div
                [ngClass]="nested
                  ? (isDarkMode ? 'rounded-lg px-2 py-1.5 bg-gray-900/40' : 'rounded-lg px-2 py-1.5 bg-white/70')
                  : (isDarkMode ? 'rounded-xl border px-3 py-2 border-gray-800 bg-gray-800/60' : 'rounded-xl border px-3 py-2 border-gray-100 bg-gray-50/80')">
                <div class="grid grid-cols-[minmax(0,1fr)_auto_8.5rem] items-center gap-3">
                  <div class="min-w-0 flex items-center gap-1.5">
                    <button *ngIf="agent.hasDetail" type="button"
                      (click)="toggleAgentExpansion(agent.key)"
                      class="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded transition-colors"
                      [ngClass]="isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-700'"
                      [title]="expandedAgents.has(agent.key) ? 'Colapsar detalle' : 'Ver detalle'">
                      <span class="text-[10px] leading-none">{{ expandedAgents.has(agent.key) ? '▾' : '▸' }}</span>
                    </button>
                    <span *ngIf="!agent.hasDetail" class="w-4 flex-shrink-0"></span>
                    <p class="truncate" [ngClass]="nested ? 'text-xs font-medium' : 'text-sm font-semibold'">{{ agent.name }}</p>
                  </div>
                  <p class="text-right text-xs tabular-nums whitespace-nowrap" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
                    {{ agent.unitsText }}
                  </p>
                  <div class="text-right">
                    <p class="tabular-nums" [ngClass]="nested ? 'text-xs font-medium' : 'text-sm font-semibold'">
                      {{ formatCurrency(agent.subtotal, currency) }}
                    </p>
                  </div>
                </div>

                <!-- Sub-filas: un concepto = una fila, con sus unidades y su importe.
                     Se pintan con uno o con varios conceptos (#649). -->
                <div *ngIf="agent.hasDetail && expandedAgents.has(agent.key)" class="mt-2 space-y-1 border-t pt-2"
                  [ngClass]="isDarkMode ? 'border-gray-700' : 'border-gray-200'">
                  <div *ngFor="let line of agent.lines" class="billy-breakdown-line grid grid-cols-[minmax(0,1fr)_auto_8.5rem] items-center gap-3 pl-5">
                    <p class="text-xs truncate" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
                      · {{ line.label }}
                    </p>
                    <p class="text-right text-xs tabular-nums whitespace-nowrap" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
                      {{ line.unitsText }}
                    </p>
                    <p class="text-right text-xs tabular-nums font-medium" [ngClass]="isDarkMode ? 'text-gray-300' : 'text-gray-700'">
                      {{ formatCurrency(line.subtotal, currency) }}
                    </p>
                  </div>
                </div>
              </div>
            </ng-template>
          </div>

          <!-- Minimum applied / correction / total — keep exactly as before -->
          <div
            *ngIf="consumption.minimum_applied > 0 || consumption.correction_amount > 0"
            class="mt-3 space-y-1 border-t px-3 pt-3 text-sm"
            [ngClass]="isDarkMode ? 'border-gray-800' : 'border-gray-100'">
            <div
              *ngIf="consumption.minimum_applied > 0"
              class="grid grid-cols-[minmax(0,1fr)_8.5rem] items-center gap-3"
              [ngClass]="isDarkMode ? 'text-gray-300' : 'text-gray-600'">
              <span>Applied minimum</span>
              <span class="text-right font-medium tabular-nums">+{{ formatCurrency(consumption.minimum_applied, consumption.currency) }}</span>
            </div>
            <div
              *ngIf="consumption.correction_amount > 0"
              class="grid grid-cols-[minmax(0,1fr)_8.5rem] items-center gap-3"
              [ngClass]="isDarkMode ? 'text-gray-300' : 'text-gray-600'">
              <span>Correction</span>
              <span class="text-right font-medium tabular-nums">-{{ formatCurrency(consumption.correction_amount, consumption.currency) }}</span>
            </div>
          </div>

          <div
            *ngIf="showFinancials"
            class="mt-3 grid grid-cols-[minmax(0,1fr)_8.5rem] items-end gap-3 border-t px-3 pt-3"
            [ngClass]="isDarkMode ? 'border-gray-800' : 'border-gray-100'">
            <div class="min-w-0">
              <p class="text-xs" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">{{ consumption.partial ? 'Total for your agents' : 'Period total' }}</p>
              <p *ngIf="consumption.partial" class="mt-1 text-xs" [ngClass]="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
                Only the agents you can see{{ consumption.partial_scope ? ' in ' + consumption.partial_scope : '' }}. The account total, minimums, discounts and prepaid balance aren't included.
              </p>
            </div>
            <p class="text-right text-3xl font-light tracking-tight tabular-nums">
              {{ formatCurrency(consumption.partial ? consumption.partial_lines_total : consumption.total, consumption.currency) }}
            </p>
          </div>
        </div>
      </div>
    </ng-container>
  </div>

  <div
    class="billy-bar-toggle flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 transition sm:px-6"
    [ngClass]="[
      collapsed ? 'border-t' : 'order-first border-b billy-bar-toggle-header',
      isDarkMode ? 'border-gray-800 hover:bg-gray-800/60' : 'border-gray-100 hover:bg-gray-50'
    ]"
    role="button"
    tabindex="0"
    [attr.aria-expanded]="!collapsed"
    [attr.aria-label]="collapsed ? 'Expand consumption bar' : 'Close consumption bar'"
    (click)="toggleCollapsed()"
    (keydown.enter)="toggleCollapsed()"
    (keydown.space)="$event.preventDefault(); toggleCollapsed()">

    <span class="relative flex h-2 w-2 shrink-0">
      <span *ngIf="!error" class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
      <span class="relative inline-flex h-2 w-2 rounded-full" [ngClass]="error ? 'bg-amber-500' : 'bg-emerald-500'"></span>
    </span>

    <span
      class="shrink-0 text-[11px] font-semibold uppercase tracking-[0.18em]"
      [ngClass]="isDarkMode ? 'text-emerald-400' : 'text-emerald-600'">
      Consumption
    </span>

    <span class="billy-ticker relative h-4 min-w-0 flex-1 overflow-hidden text-xs" [ngClass]="isDarkMode ? 'text-gray-300' : 'text-gray-500'">
      <span
        *ngIf="collapsed && tickerVisible && currentTickerMessage"
        class="billy-ticker-item absolute inset-0 flex items-center truncate">
        {{ currentTickerMessage }}
      </span>
      <span *ngIf="!collapsed" class="flex items-center truncate">{{ consumption?.period_label || 'Billy' }}</span>
    </span>

    <!-- Saldo prepago siempre a la vista, sin abrir la barra -->
    <span
      *ngIf="prepaidGauge as gauge"
      class="billy-prepaid-header flex shrink-0 items-center gap-2"
      [attr.title]="'Prepaid balance: ' + formatCurrency(gauge.availableEur, consumption?.currency || 'EUR') + ' left of ' + formatCurrency(gauge.sizeEur, consumption?.currency || 'EUR')">
      <span class="hidden text-[11px] font-medium uppercase tracking-[0.12em] md:inline" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">Balance</span>
      <span class="relative h-1.5 w-14 overflow-hidden rounded-full sm:w-24" [ngClass]="isDarkMode ? 'bg-gray-700' : 'bg-gray-200'">
        <span class="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-in-out" [ngClass]="prepaidToneClass(gauge.tone)" [style.width.%]="gauge.percent"></span>
      </span>
      <span class="text-xs font-semibold tabular-nums" [ngClass]="gauge.tone === 'critical' ? 'text-red-500' : gauge.tone === 'low' ? 'text-amber-500' : (isDarkMode ? 'text-gray-100' : 'text-gray-900')">{{ formatCurrency(gauge.availableEur, consumption?.currency || 'EUR') }}</span>
      <span class="hidden text-[11px] tabular-nums sm:inline" [ngClass]="isDarkMode ? 'text-gray-400' : 'text-gray-500'">{{ gauge.percent }} %</span>
    </span>

    <button
      type="button"
      class="shrink-0 rounded-full p-1.5 text-gray-400 transition focus:outline-none focus:ring-2 focus:ring-emerald-500"
      [ngClass]="isDarkMode ? 'hover:bg-gray-800 hover:text-gray-200' : 'hover:bg-gray-100 hover:text-gray-700'"
      title="Refresh consumption"
      aria-label="Refresh consumption"
      [disabled]="loading"
      (click)="$event.stopPropagation(); refreshCurrentFilter()">
      <svg class="h-4 w-4" [class.animate-spin]="loading" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M16.023 9.348h4.992m0 0V4.356m0 4.992-3.181-3.183a8.25 8.25 0 1 0 2.182 7.777" />
      </svg>
    </button>

    <span
      class="billy-chevron shrink-0 rounded-full p-1.5"
      [ngClass]="isDarkMode ? 'text-gray-300' : 'text-gray-500'"
      [attr.title]="collapsed ? 'Expand consumption bar' : 'Close consumption bar'">
      <svg
        *ngIf="collapsed"
        class="billy-chevron-icon h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
      </svg>
      <svg
        *ngIf="!collapsed"
        class="billy-chevron-icon h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M6 18 18 6M6 6l12 12" />
      </svg>
    </span>
  </div>
</aside>
